import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, Input, OnDestroy,
  OnInit, Optional, ViewContainerRef
} from '@angular/core';
import { DiscussionService } from './discussion.service';
import { Observable, Subscription } from 'rxjs';
import { DiscussionParticipantDialogComponent } from '../participant/discussion-participant-dialog.component';
import { DiscussionComponent } from './discussion.component';
import { SnackbarDataInterface } from '../../common/snackbar/snackbar-data-interface';
import { AppComponent } from '../../app.component';
import { DiscussionModel } from '../../common/models/discussion-model';
import { ConfigProvider} from "../../config-provider.service";
import { ShareService } from '../share/share.service';
import { ChatCounterService } from '../message/chat-counter.service';
import { NotificationService } from '../../notifications/notification.service';
import { MobileChatComponent } from '../../mobile/chat/mobile.chat.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization } from "../../common/models/organization";
import { User } from "../../common/models/user";
import { Notification } from "../../common/models/notification";
import { ModuleContentComponent } from "../../common/components/module-content/module-content";
import { AsyncPipe, NgForOf, NgIf, NgOptimizedImage, NgTemplateOutlet } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { MatButtonModule } from "@angular/material/button";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { MatDivider } from "@angular/material/divider";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MatListItem, MatNavList } from "@angular/material/list";
import { DateClosestPipe } from "../../common/pipes/date-closest";
import { EmojifyPipe } from "../pipe/emojify.pipe";
import { NgStringPipesModule } from "ngx-pipes";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MatLine } from "@angular/material/core";

@Component({
  selector: 'app-chat-discussion-list',
  templateUrl: './discussion-list.component.html',
  styleUrls: ['./discussion-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ModuleContentComponent,
    NgIf,
    NgTemplateOutlet,
    DiscussionComponent,
    TranslateModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuTrigger,
    MatIconModule,
    MatMenuModule,
    AsyncPipe,
    MatDivider,
    MatProgressSpinner,
    MatNavList,
    MatListItem,
    NgForOf,
    DateClosestPipe,
    EmojifyPipe,
    NgStringPipesModule,
    FlexLayoutModule,
    MatLine,
    NgOptimizedImage,
  ],
})
export class DiscussionListComponent implements OnInit, OnDestroy {
    @Input()
    public mobileDisplay = false;
    @Input()
    public organization: Organization | null = null;
    @Input()
    public athleteUser: User = {} as User;
    @Input()
    public discussionContainer!: ViewContainerRef;

    public discussions$: Observable<DiscussionModel[]> | null = null;

    private component: DiscussionComponent | null = null
    private subscriptions: Subscription[] = [];

    constructor(
        private app: AppComponent,
        private discussionService: DiscussionService,
        private discussionParticipantDialog: MatDialog,
        public conf: ConfigProvider,
        private shareService: ShareService,
        public chatCounterService: ChatCounterService,
        private changeDetectorRef: ChangeDetectorRef,
        private notificationService: NotificationService,
    ) {
        this.subscribeToNotificationClicked();
    }

    public populateDiscussions(): void {
        this.discussions$ = this.discussionService.findAllByUser(this.athleteUser); // user
        this.changeDetectorRef.detectChanges();
    }

    public onNewDiscussionClicked() {
        const dialog: MatDialogRef<DiscussionParticipantDialogComponent> = this.discussionParticipantDialog.open(DiscussionParticipantDialogComponent, {
            data: {
                user: this.athleteUser, //user
                organization: this.organization,
            }
        });

        this.subscriptions.push(
            dialog.afterClosed().subscribe((data: DiscussionModel) => {
                if (data) {
                    this.openDiscussion(data);
                }
            })
        );
    }

    public ngOnInit(): void {
        this.subscriptions.push(
            this.shareService.sharedEvent.subscribe(() => {
                if (this.component) {
                    this.component.getMessages();
                }
            })
        );

        this.subscriptions.push(
            this.discussionService.discussionDeletedEvent.subscribe(() => {
                this.populateDiscussions();
            })
        );

        if (this.mobileDisplay) {
            this.populateDiscussions();
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public openDiscussion(discussion: DiscussionModel) {
        if (this.component) {
            this.component.closeDiscussion();
        }

        this.loadComponents(discussion);
    }

    private subscribeToNotificationClicked(): void {
        this.subscriptions.push(
            this.notificationService.subject.subscribe((notification: Notification) => {

                if (notification.data?.discussion_id) {
                    this.subscriptions.push(
                        this.discussionService.findOneById(this.athleteUser, notification.data.discussion_id).subscribe((discussionModel: DiscussionModel) => {
                            this.openDiscussion(discussionModel);
                        })
                    );
                }
            })
        );
    }

    private loadComponents(discussion: DiscussionModel) {
      const ref = this.mobileDisplay ? this.discussionContainer : this.app.getChatDiscussionRef();

        ref?.clear();
        const componentRef = ref?.createComponent(DiscussionComponent, { injector: ref.injector });
        this.component = componentRef?.instance as DiscussionComponent;
        this.component.viewRef = componentRef;
        this.component.discussion = discussion;
        this.component.athleteUser = this.athleteUser; //user
        this.component.mobileDisplay = this.mobileDisplay;
        if (this.organization)
          this.component.organization = this.organization;

        this.subscriptions.push(
            this.component.markMessagesAsSeen().subscribe(() => {
                this.chatCounterService.refreshChatCounter();
            })
        );
    }
}
